import { Injectable } from '@angular/core';
import { FirebaseService } from './firebase.service';
import { AngularFireAuth } from '@angular/fire/auth';
import { fromPromise } from 'rxjs/internal/observable/fromPromise';
import { BehaviorSubject, Observable } from 'rxjs';
import { take, shareReplay } from 'rxjs/operators';
import { DebugService, DebugLevel } from '../debug/debug.service';

export interface AuthState {
  user: AppUser;
  token: string;
  authenticated: boolean;
}
export interface AppUser {
  isAnonymous: boolean;
  name: string;
  given_name: string;
  email: string;
  emailVerified: boolean;
  photoURL: string;
  uid: string;
  isNewUser: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  /** App current user stream */
  private user: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  /** App global auth state */
  private authenticated: boolean = false;

  constructor(private debugService: DebugService, private fireAuth: AngularFireAuth, private firebaseService: FirebaseService) {
    this.fireAuth.onAuthStateChanged(
      (user) => {
        if (user !== null) {
          fromPromise(user.getIdToken(true)).subscribe((token: string) => {
            this.debugService.debug(DebugLevel.INFO, 'User auth state changed to true with token: ', token);
            this.authenticated = true;
            this.user.next(user);
          });
        } else {
          this.debugService.debug(DebugLevel.INFO, 'User auth state changed to false');
          this.authenticated = false;
          this.user.next(null);
        }
      },
      (err) => {
        this.debugService.debug(DebugLevel.ERROR, 'Error on auth state observable', null);
        this.debugService.debug(DebugLevel.DEBUG, 'Error on auth state observable', err);
      }
    );
  }
  /**
   * Realiza un login de firebase con los datos dels formulario
   * @param formValues must contain {email, password}
   */
  public Login(formValues): Observable<any> {
    return this.firebaseService.signinUser(formValues.email, formValues.password).pipe(take(1));
  }

  /** Realiza el login mediante la cuenta de gmail google */
  public async GoogleLogin(): Promise<boolean> {
    this.debugService.debug(DebugLevel.TRACE, 'AuthService[ GoogleLogin ]', 'init');
    try {
      const user = await this.firebaseService.doGoogleLogin();
      this.user.next(user);
      this.debugService.debug(DebugLevel.TRACE, 'AuthService[ GoogleLogin ]', 'end');
      return true;
    } catch (error) {
      this.debugService.debug(DebugLevel.DEBUG, 'Error doing google login', error);
      this.debugService.debug(DebugLevel.ERROR, 'Error doing google login', null);
      return false;
    }
  }
  /** Returns global app auth value */
  public isAuthenticated(): boolean {
    return this.authenticated;
  }
  /** Returns app user stream */
  public getUser(): Observable<firebase.User> {
    return this.user.asObservable().pipe(shareReplay(1));
  }
}
